import { useEffect, useRef, useState } from 'react';

const SNOW_CONTAINER_CLASS = 'snow-container';
const STORAGE_KEY = 'snow-animation-enabled';
const MODE_STORAGE_KEY = 'snow-animation-mode';

export type NewYearMode = 'snowflake' | 'crazy' | 'santa';

const AUDIO_PATHS: Record<NewYearMode, string | null> = {
  snowflake: null,
  crazy: '/audio/jbiv.mp3',
  santa: '/audio/jbp.mp3',
};

export const useNewYearMood = (isAdmin: boolean, isNewYearSeason: boolean) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    try {
      const item = window.localStorage.getItem(STORAGE_KEY);
      return item ? JSON.parse(item) : false;
    } catch (error) {
      console.error('Error reading localStorage for snow animation:', error);
      return false;
    }
  });

  const [mode, setMode] = useState<NewYearMode>(() => {
    try {
      const item = window.localStorage.getItem(MODE_STORAGE_KEY);
      return (item as NewYearMode) || 'snowflake';
    } catch {
      return 'snowflake';
    }
  });

  // Refs
  const snowflakesRef = useRef<HTMLDivElement[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const styleElementRef = useRef<HTMLStyleElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toggle = () => {
    if (!isAdmin || !isNewYearSeason) return;
    setIsEnabled(prev => {
      const newState = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      } catch (error) {
        console.error('Error saving snow animation state:', error);
      }
      return newState;
    });
  };

  const changeMode = (newMode: NewYearMode) => {
    setMode(newMode);
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, newMode);

      // If enabled, changing mode might play/stop audio
      // Logic handled in useEffect dependency
    } catch (error) {
      console.error('Error saving mode:', error);
    }
  };

  // Audio Management
  useEffect(() => {
    if (!isEnabled || !isAdmin || !isNewYearSeason) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      return;
    }

    const audioPath = AUDIO_PATHS[mode];

    // Stop current audio if it doesn't match new path
    if (audioRef.current && (!audioPath || !audioRef.current.src.endsWith(audioPath))) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (audioPath && !audioRef.current) {
      const audio = new Audio(audioPath);
      audio.loop = false;
      audio.volume = 0.5;
      audio.play().catch(e => console.warn('Audio play failed (user interaction needed?):', e));
      audioRef.current = audio;
    }
  }, [isEnabled, isAdmin, isNewYearSeason, mode]);

  useEffect(() => {
    if (isNewYearSeason) return;
    setIsEnabled(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(false));
    } catch (error) {
      console.error('Error saving snow animation state:', error);
    }
    teardown();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, [isNewYearSeason]);

  // Teardown helper
  const teardown = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);

    snowflakesRef.current.forEach(s => {
      if ((s as any)._timeoutId) clearTimeout((s as any)._timeoutId);
      s.remove();
    });
    snowflakesRef.current = [];

    if (containerRef.current) {
      containerRef.current.remove();
      containerRef.current = null;
    }
    const existingContainer = document.querySelector(`.${SNOW_CONTAINER_CLASS}`);
    if (existingContainer) existingContainer.remove();

    if (styleElementRef.current) {
      styleElementRef.current.remove();
      styleElementRef.current = null;
    }
  };

  // Lifecycle cleanup
  useEffect(() => {
    return () => {
      teardown();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Snow Animation Logic
  useEffect(() => {
    const isActive = isEnabled && isAdmin && isNewYearSeason;

    if (isActive) {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      if (!styleElementRef.current) {
        const css = `
          .snow-container {
            position: fixed;
            top: 0;
            left: 0;
            overflow: hidden;
            width: 100vw;
            height: 100vh;
            z-index: 99999;
            pointer-events: none;
          }
          .snowflake {
            position: absolute;
            background-color: white;
            border-radius: 50%;
            opacity: 0.8;
            pointer-events: none;
          }
          @keyframes fall {
            0% { opacity: 0; transform: translateY(0); }
            10% { opacity: 1; }
            100% { opacity: 0.5; transform: translateY(100vh); }
          }
          @keyframes diagonal-fall {
            0% { opacity: 0; transform: translate(0, 0); }
            10% { opacity: 1; }
            100% { opacity: 0.25; transform: translate(10vw, 100vh); }
          }
        `;
        const styleId = 'snow-animation-styles';
        let el = document.getElementById(styleId) as HTMLStyleElement;
        if (!el) {
          el = document.createElement('style');
          el.id = styleId;
          el.textContent = css;
          document.head.appendChild(el);
        }
        styleElementRef.current = el;
      }

      if (!containerRef.current || !document.body.contains(containerRef.current)) {
        let container = document.querySelector(`.${SNOW_CONTAINER_CLASS}`) as HTMLDivElement;
        if (!container) {
          container = document.createElement('div');
          container.classList.add(SNOW_CONTAINER_CLASS);
          document.body.appendChild(container);
        }
        containerRef.current = container;
      }

      const particlesPerThousandPixels = 0.1;
      const fallSpeed = 0.625;
      const maxSnowflakes = 200;

      const resetSnowflake = (snowflake: HTMLDivElement) => {
        const size = Math.random() * 5 + 1;
        const viewportWidth = window.innerWidth - size;
        const viewportHeight = window.innerHeight;

        snowflake.style.width = `${size}px`;
        snowflake.style.height = `${size}px`;
        snowflake.style.left = `${Math.random() * viewportWidth}px`;
        snowflake.style.top = `-${size}px`;

        const animationDuration = (Math.random() * 3 + 2) / fallSpeed;
        snowflake.style.animationDuration = `${animationDuration}s`;
        snowflake.style.animationTimingFunction = 'linear';
        snowflake.style.animationName = Math.random() < 0.5 ? 'fall' : 'diagonal-fall';

        const timeoutId = setTimeout(() => {
          if (!containerRef.current || !document.body.contains(containerRef.current)) return;

          if (!intervalRef.current) {
            snowflake.remove();
            const index = snowflakesRef.current.indexOf(snowflake);
            if (index > -1) snowflakesRef.current.splice(index, 1);
            return;
          }

          if (parseInt(snowflake.style.top, 10) < viewportHeight) {
            resetSnowflake(snowflake);
          } else {
            snowflake.remove();
            const index = snowflakesRef.current.indexOf(snowflake);
            if (index > -1) snowflakesRef.current.splice(index, 1);
          }
        }, animationDuration * 1000);

        (snowflake as any)._timeoutId = timeoutId;
      };

      const createSnowflake = () => {
        if (!containerRef.current) return;
        if (snowflakesRef.current.length < maxSnowflakes) {
          const snowflake = document.createElement('div');
          snowflake.classList.add('snowflake');
          snowflakesRef.current.push(snowflake);
          containerRef.current.appendChild(snowflake);
          resetSnowflake(snowflake);
        }
      };

      const generateSnowflakes = () => {
        const numberOfParticles =
          Math.ceil((window.innerWidth * window.innerHeight) / 1000) * particlesPerThousandPixels;
        const interval = 5000 / numberOfParticles;

        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
          if (!document.hidden && snowflakesRef.current.length < maxSnowflakes) {
            requestAnimationFrame(createSnowflake);
          }
        }, interval);
      };

      generateSnowflakes();

      const handleVisibilityChange = () => {
        if (document.hidden) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;

          snowflakesRef.current.forEach(s => {
            if ((s as any)._timeoutId) clearTimeout((s as any)._timeoutId);
            s.remove();
          });
          snowflakesRef.current = [];
        } else {
          generateSnowflakes();
        }
      };

      const handleResize = () => {
        generateSnowflakes();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('resize', handleResize);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('resize', handleResize);
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (!cleanupTimeoutRef.current && containerRef.current) {
        cleanupTimeoutRef.current = setTimeout(() => {
          teardown();
        }, 12000);
      }
    }
  }, [isEnabled, isAdmin, isNewYearSeason]); // audioRef and mode handled in separate effect

  return {
    isEnabled: isEnabled && isAdmin && isNewYearSeason,
    toggle,
    mode,
    setMode: changeMode,
  };
};
